import {
  FilterCheckbox,
  FilterDateRange,
  FilterRadio,
  SoDataTableColumnType,
  SoDataTableHeader,
  FilterType,
} from "@/types/components/SoDataTable"
import { SoRadioCardChartType } from "@/types/components/SoRadioCardChart"
import { Dayjs } from "dayjs"
import { SmartMetersDashboardDownloadRequestSelectedMeterType as SmartMeterType } from "@/api/backofficeRest/generated/model"
import { meterTypes } from "@/models/smartMeter/smartMeter.types"

export type TableFilters = {
  workstream: FilterCheckbox<string>
  deviceStatus: FilterCheckbox<string>
  meterType: FilterCheckbox<string>
  installedDate: FilterDateRange
  lastReadDate: FilterDateRange
  readingsReceived: FilterRadio<string>
  lastOrchestration: FilterCheckbox<string>
  orchestrationStatus: FilterCheckbox<string>
  serviceRequest: FilterCheckbox<string>
  errorCode: FilterCheckbox<string>
  errorStatus: FilterCheckbox<string>
  latestAlert: FilterCheckbox<string>
  latestAlertStatus: FilterCheckbox<string>
  latestAlertDate: FilterDateRange
  firmwareStatus: FilterCheckbox<string>
  firmwareVersion: FilterCheckbox<string>
  gbcsVersion: FilterCheckbox<string>
  manufacturer: FilterCheckbox<string>
  model: FilterCheckbox<string>
  region: FilterCheckbox<string>
  smetsVersion: FilterCheckbox<string>
  paymentMode: FilterCheckbox<string>
  variant: FilterCheckbox<string>
  ssd: FilterDateRange
}

export type TopFilter = {
  name: string
  label: string
  type: SoRadioCardChartType
  percentage?: number
  count?: number
}

export interface SmartMeterFilterTypes {
  workstreams?: string[]
  deviceStatuses?: string[]
  meterType?: string[]
  installedDateFrom?: Dayjs
  installedDateTo?: Dayjs
  lastReadDateFrom?: Dayjs
  lastReadDateTo?: Dayjs
  readingsReceived?: boolean
  lastOrchestrations?: string[]
  orchestrationStatuses?: string[]
  serviceRequests?: string[]
  errorCodes?: string[]
  errorStatuses?: string[]
  alerts?: string[]
  alertStatuses?: string[]
  alertDateFrom?: Dayjs
  alertDateTo?: Dayjs
  firmwareStatuses?: string[]
  firmwareVersions?: string[]
  gbcsVersions?: string[]
  manufacturers?: string[]
  models?: string[]
  regions?: string[]
  smetsVersions?: string[]
  paymentModes?: string[]
  variants?: string[]
  ssdFrom?: Dayjs
  ssdTo?: Dayjs
}

export const ReadingsReceived: Record<string, string> = {
  Received: "Received",
  Never: "Never",
}

export const topFilters: TopFilter[] = [
  {
    name: "uncommissioned",
    label: "Uncommissioned",
    type: "critical",
  },
  {
    name: "inherited",
    label: "Inherited",
    type: "critical",
  },
  {
    name: "inlifeissue",
    label: "In-life Issue",
    type: "critical",
  },
  {
    name: "working",
    label: "Communicating",
    type: "normal",
  },
]

export const headers: SoDataTableHeader[] = [
  {
    title: "pk",
    key: "pk",
    isVisible: false,
  },
  {
    title: "Meter Type",
    key: "meterType",
    isVisible: false,
  },
  {
    title: "MPXN",
    key: "mpxn",
    sortable: false,
  },
  {
    title: "MSN",
    key: "msn",
    sortable: false,
  },
  {
    title: "Workstream",
    key: "workstreamStatuses",
    type: SoDataTableColumnType.STATUSES,
    sortKey: "workstream",
  },
  {
    title: "Device ID",
    key: "deviceId",
    sortKey: "device_id",
  },
  {
    title: "Device Status",
    key: "deviceStatus",
    sortKey: "dev_status",
  },
  {
    title: "Region",
    key: "region",
    sortKey: "region",
  },
  {
    title: "Last Orchestration",
    key: "lastOrchestration",
    sortKey: "last_orchestration",
  },
  {
    title: "Orchestration Status",
    key: "orchestrationStatus",
    sortKey: "orchestration_status",
  },
  {
    title: "Service Request",
    key: "serviceRequest",
    sortKey: "srv",
  },
  {
    title: "Error Code",
    key: "errorCode",
    sortKey: "error",
  },
  {
    title: "Error Status",
    key: "errorStatus",
    sortKey: "error_status",
  },
  {
    title: "Install Date",
    key: "installationDate",
    type: SoDataTableColumnType.DATE,
    sortKey: "installation_date",
  },
  {
    title: "SSD",
    key: "ssd",
    type: SoDataTableColumnType.DATE,
  },
  {
    title: "Last\u00A0DCC Read\u00A0Date",
    key: "readingDateTime",
    type: SoDataTableColumnType.DATE,
    sortKey: "last_reading_date_time",
  },
  {
    title: "Latest Reading",
    key: "latestReading",
    sortKey: "last_reading",
  },
  {
    title: "Last Alert",
    key: "latestAlert",
    sortKey: "latest_alert_code",
  },
  {
    title: "Last Alert Date",
    key: "latestAlertDate",
    type: SoDataTableColumnType.DATE,
    sortKey: "latest_alert_date",
  },
  {
    title: "Last Alert Status",
    key: "latestAlertStatus",
    sortKey: "latest_alert_status",
  },
  {
    title: "Firmware Status",
    key: "firmwareStatus",
    sortKey: "firmware_status",
  },
  {
    title: "Firmware Version",
    key: "firmwareVersion",
    sortKey: "firmware_version",
  },
  {
    title: "Manufacturer",
    key: "manufacturerName",
    sortable: false,
  },
  {
    title: "Model",
    key: "modelName",
    sortable: false,
  },
  {
    title: "Variant",
    key: "variant",
    sortKey: "variant",
  },
  {
    title: "Payment Mode",
    key: "paymentMode",
    sortKey: "payment_mode",
  },
  {
    title: "SMETS/CHTS Version",
    key: "smetsVersion",
    sortKey: "smets_version",
  },
  {
    title: "GBCS Version",
    key: "gbcsVersion",
    sortKey: "gbcs_version",
  },
  {
    title: "Comms Hub ID",
    key: "commsHubId",
    sortable: false,
  },
]

export const tableFilters: TableFilters = {
  workstream: {
    type: FilterType.CHECKBOX,
    label: "Workstream",
    options: {
      working: "Communicating",
      uncommissioned: "Uncommissioned",
      inherited: "Inherited",
      inlifeissue: "In-life Issue",
      criticalalert: "Critical Alert",
      firmwareexpired: "Firmware Expired",
    },
    value: [],
    expandable: true,
  },
  deviceStatus: {
    type: FilterType.CHECKBOX,
    label: "Device Status",
    options: {
      INSTALLED_NOT_COMMISSIONED: "Installed not Commissioned",
      COMMISSIONED: "Commissioned",
      DECOMMISSIONED: "Decommissioned",
      PENDING: "Pending",
      SUSPENDED: "Suspended",
      WHITELISTED: "Whitelisted",
    },
    value: [],
    expandable: true,
  },
  meterType: {
    type: FilterType.CHECKBOX,
    label: "Meter Type",
    options: {
      ESME: "Electric",
      GSME: "Gas",
    },
    value: [],
    expandable: true,
  },
  region: {
    type: FilterType.CHECKBOX,
    label: "Region",
    options: {},
    value: [],
    expandable: true,
  },
  lastOrchestration: {
    type: FilterType.CHECKBOX,
    label: "Last Orchestration",
    options: {},
    value: [],
    expandable: true,
  },
  orchestrationStatus: {
    type: FilterType.CHECKBOX,
    label: "Orchestration Status",
    options: {},
    value: [],
    expandable: true,
  },
  serviceRequest: {
    type: FilterType.CHECKBOX,
    label: "Service Request",
    options: {},
    value: [],
    expandable: true,
  },
  errorCode: {
    type: FilterType.CHECKBOX,
    label: "Error Code",
    options: {},
    value: [],
    expandable: true,
  },
  errorStatus: {
    type: FilterType.CHECKBOX,
    label: "Error Status",
    options: {},
    value: [],
    expandable: true,
  },
  installedDate: {
    type: FilterType.DATE_RANGE,
    label: "Install Date",
    value: { from: null, to: null },
    expandable: true,
  },
  ssd: {
    type: FilterType.DATE_RANGE,
    label: "SSD",
    value: { from: null, to: null },
    expandable: true,
  },
  lastReadDate: {
    type: FilterType.DATE_RANGE,
    label: "Last DCC Read Date",
    value: { from: null, to: null },
    expandable: true,
  },
  readingsReceived: {
    type: FilterType.RADIO,
    label: "Readings Received",
    options: ReadingsReceived,
    value: null,
    expandable: true,
  },
  latestAlert: {
    type: FilterType.CHECKBOX,
    label: "Last Alert",
    options: {},
    value: [],
    expandable: true,
  },
  latestAlertDate: {
    type: FilterType.DATE_RANGE,
    label: "Last Alert Date",
    value: { from: null, to: null },
    expandable: true,
  },
  latestAlertStatus: {
    type: FilterType.CHECKBOX,
    label: "Last Alert Status",
    options: {},
    value: [],
    expandable: true,
  },
  firmwareStatus: {
    type: FilterType.CHECKBOX,
    label: "Firmware Status",
    options: {},
    value: [],
    expandable: true,
  },
  firmwareVersion: {
    type: FilterType.CHECKBOX,
    label: "Firmware Version",
    options: {},
    value: [],
    expandable: true,
  },
  manufacturer: {
    type: FilterType.CHECKBOX,
    label: "Manufacturer",
    options: {},
    value: [],
    expandable: true,
    truncateText: true,
  },
  model: {
    type: FilterType.CHECKBOX,
    label: "Model",
    options: {},
    value: [],
    expandable: true,
    truncateText: true,
  },
  variant: {
    type: FilterType.CHECKBOX,
    label: "Variant",
    options: {},
    value: [],
    expandable: true,
  },
  paymentMode: {
    type: FilterType.CHECKBOX,
    label: "Payment Mode",
    options: {},
    value: [],
    expandable: true,
  },
  smetsVersion: {
    type: FilterType.CHECKBOX,
    label: "SMETS/CHTS Version",
    options: {},
    value: [],
    expandable: true,
  },
  gbcsVersion: {
    type: FilterType.CHECKBOX,
    label: "GBCS Version",
    options: {},
    value: [],
    expandable: true,
  },
}

export function defineSelectedMeterType(
  value: Array<string> | undefined,
): SmartMeterType | undefined {
  if (value?.includes(meterTypes.ELEC) && value?.includes(meterTypes.GAS))
    return SmartMeterType.BOTH
  else if (value?.includes(meterTypes.GAS)) {
    return SmartMeterType.GAS
  } else if (value?.includes(meterTypes.ELEC)) {
    return SmartMeterType.ELEC
  } else return undefined
}
